import React, { useState } from 'react';
import {
  TextField,
  MenuItem,
  TextFieldProps,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useField, useFormikContext } from 'formik';
import WidgetSettingSelect from './WidgetSetting/WidgetSettingSelect';

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    borderLeft: '4px solid #000',
    paddingLeft: 12,
    paddingTop: 10,
    marginBottom: 1,
    paddingBottom: 6,

    '&.focused': {
      borderLeftColor: theme.palette.primary.main,
    },

    '&.error': {
      borderLeftColor: theme.palette.error.main,
    },

    '&.disabled': {
      borderLeftColor: '#ccc',
    },

    '&.dense': {
      paddingTop: 6,
      paddingBottom: 4,
    },
  },

  flat: {
    position: 'relative',
    borderLeft: 'none',
    paddingLeft: 0,
    paddingTop: 10,
    marginBottom: 1,
    paddingBottom: 6,
    '&.dense': {
      paddingTop: 6,
      paddingBottom: 4,
    },
  },

  dashboard: {
    position: 'relative',
    paddingTop: 4,
    marginBottom: 1,
    paddingBottom: 6,
  },

  textField: {
    margin: 0,
  },

  inputRoot: {
    padding: 0,
  },

  input: {
    padding: '4px 0',
    fontSize: 14,
    lineHeight: 1.4,
  },

  inputDense: {
    padding: '2px 0',
    fontSize: 13,
  },

  label: {
    fontSize: 12,
    transformOrigin: 'top left',
  },

  labelDense: {
    fontSize: 11,
  },

  helperText: {
    margin: '2px 0 0 0',
    fontSize: 12,
  },

  dashboardLabel: {
    display: 'block',
    fontSize: '0.8125rem',
    fontWeight: 400,
    letterSpacing: 0,
    color: 'var(--text-secondary, #64748b)',
    marginBottom: '0.4rem',
    lineHeight: 1.45,
  },
}));

type Option = {
  label: string;
  value: string;
};

type BaseProps = Omit<TextFieldProps, 'select'> & {
  options: Option[];
  dense?: boolean;
  flat?: boolean;
  dashboardChrome?: boolean;
};

const PlainSelect: React.FC<
  BaseProps & {
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    errorText?: string;
  }
> = ({ options, dense, flat = false, dashboardChrome = false, value, onChange, errorText, disabled, ...props }) => {
  const classes = useStyles();
  const [focused, setFocused] = useState(false);
  const showError = Boolean(errorText);
  const selectAriaLabel =
    typeof props["aria-label"] === "string"
      ? props["aria-label"]
      : typeof props.label === "string"
        ? props.label
        : undefined;

  if (dashboardChrome) {
    return (
      <div
        id={typeof props.id === "string" ? props.id : undefined}
        className={clsx(classes.dashboard, "widget-setting", "app-dashboard-form-chrome")}
      >
        {props.label ? <label className={classes.dashboardLabel}>{props.label}</label> : null}
        <WidgetSettingSelect
          value={value ?? ""}
          disabled={disabled}
          options={options}
          placeholder={typeof props.placeholder === "string" ? props.placeholder : undefined}
          aria-label={selectAriaLabel}
          onChange={(nextValue) =>
            onChange?.({ target: { value: nextValue } } as React.ChangeEvent<HTMLInputElement>)
          }
        />
        {showError ? <div className={classes.helperText}>{errorText}</div> : null}
      </div>
    );
  }

  return (
    <div
      className={clsx(flat ? classes.flat : classes.container, {
        focused,
        error: showError,
        disabled,
        dense,
      })}
    >
      <TextField
        {...props}
        select
        fullWidth
        disabled={disabled}
        variant="standard"
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        error={showError}
        helperText={showError ? errorText : undefined}
        FormHelperTextProps={{ className: classes.helperText }}
        InputProps={{
          disableUnderline: true,
          classes: {
            root: classes.inputRoot,
            input: clsx(classes.input, dense && classes.inputDense),
          },
        }}
        InputLabelProps={{
          classes: {
            root: clsx(classes.label, dense && classes.labelDense),
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
};

const FormikSelect: React.FC<
  BaseProps & { name: string }
> = ({ name, options, dense, flat = false, dashboardChrome = false, disabled, ...props }) => {
  const classes = useStyles();
  const [focused, setFocused] = useState(false);
  const [field, meta, helpers] = useField(name);

  const showError = Boolean(meta.touched && meta.error);
  const selectAriaLabel =
    typeof props["aria-label"] === "string"
      ? props["aria-label"]
      : typeof props.label === "string"
        ? props.label
        : undefined;

  if (dashboardChrome) {
    return (
      <div className={clsx(classes.dashboard, "widget-setting", "app-dashboard-form-chrome")}>
        {props.label ? <label className={classes.dashboardLabel}>{props.label}</label> : null}
        <WidgetSettingSelect
          value={field.value ?? ""}
          disabled={disabled}
          options={options}
          placeholder={typeof props.placeholder === "string" ? props.placeholder : undefined}
          aria-label={selectAriaLabel}
          onChange={(nextValue) => {
            helpers.setValue(nextValue);
            helpers.setTouched(true);
          }}
        />
        {showError ? <div className={classes.helperText}>{meta.error}</div> : null}
      </div>
    );
  }

  return (
    <div
      className={clsx(flat ? classes.flat : classes.container, {
        focused,
        error: showError,
        disabled,
        dense,
      })}
    >
      <TextField
        {...props}
        {...field}
        select
        fullWidth
        disabled={disabled}
        variant="standard"
        error={showError}
        helperText={showError ? meta.error : undefined}
        FormHelperTextProps={{ className: classes.helperText }}
        onFocus={() => setFocused(true)}
        onBlur={(e) => {
          setFocused(false);
          field.onBlur(e);
        }}
        InputProps={{
          disableUnderline: true,
          classes: {
            root: classes.inputRoot,
            input: clsx(classes.input, dense && classes.inputDense),
          },
        }}
        InputLabelProps={{
          classes: {
            root: clsx(classes.label, dense && classes.labelDense),
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </TextField>
    </div>
  );
};

const LeftBorderSelect: React.FC<
  BaseProps & {
    name?: string;
    value?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    errorText?: string;
  }
> = (props) => {
  const formik = useFormikContext<any | null>();

  if (formik && props.name) {
    return <FormikSelect {...props} name={props.name} />;
  }

  return <PlainSelect {...props} />;
};

export default LeftBorderSelect;
